import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal/Modal';
import styles from './RelatiebeheerModule.module.css';
import { ContactMoment } from './types';

interface ContactMomentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (moment: ContactMoment) => void;
  onDelete?: () => void;
  moment?: ContactMoment;
  contactId: string;
}

export const ContactMomentModal: React.FC<ContactMomentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  moment,
  contactId
}) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'meeting' as ContactMoment['type'],
    subject: '',
    description: '',
    outcome: '',
    sentiment: 'neutral' as ContactMoment['sentiment'],
    followUpRequired: false,
    nextContact: '',
    participants: [] as string[]
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (moment) {
      setFormData({
        date: typeof moment.date === 'string' ?
          moment.date.split('T')[0] :
          new Date(moment.date).toISOString().split('T')[0],
        type: moment.type || 'meeting',
        subject: moment.subject || '',
        description: moment.description || '',
        outcome: moment.outcome || '',
        sentiment: moment.sentiment || 'neutral',
        followUpRequired: moment.followUpRequired || false,
        nextContact: moment.nextContact ?
          (typeof moment.nextContact === 'string' ?
            moment.nextContact.split('T')[0] :
            new Date(moment.nextContact).toISOString().split('T')[0]) :
          '',
        participants: moment.participants || []
      });
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        type: 'meeting' as ContactMoment['type'],
        subject: '',
        description: '',
        outcome: '',
        sentiment: 'neutral' as ContactMoment['sentiment'],
        followUpRequired: false,
        nextContact: '',
        participants: []
      });
    }
  }, [moment]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Datum is verplicht';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Onderwerp is verplicht';
    }
    if (!formData.type) {
      newErrors.type = 'Type is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      // Format participants as array
      const participantsArray = Array.isArray(formData.participants)
        ? formData.participants
        : typeof formData.participants === 'string'
        ? (formData.participants as string).split(',').map((p: string) => p.trim()).filter(Boolean)
        : [];

      const momentData: ContactMoment = {
        ...formData,
        participants: participantsArray,
        date: new Date(formData.date),
        nextContact: formData.nextContact ? new Date(formData.nextContact) : undefined
      };

      onSave(momentData);
      onClose();
    }
  };

  const typeOptions = [
    { value: 'meeting', label: 'Bijeenkomst' },
    { value: 'call', label: 'Telefonisch' },
    { value: 'email', label: 'E-mail' },
    { value: 'event', label: 'Evenement' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'letter', label: 'Brief' },
    { value: 'other', label: 'Overig' }
  ];

  const sentimentOptions = [
    { value: 'very_positive', label: 'Zeer Positief', color: '#4CAF50' },
    { value: 'positive', label: 'Positief', color: '#8BC34A' },
    { value: 'neutral', label: 'Neutraal', color: '#9E9E9E' },
    { value: 'negative', label: 'Negatief', color: '#FF9800' },
    { value: 'very_negative', label: 'Zeer Negatief', color: '#F44336' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={moment ? 'Contactmoment bewerken' : 'Nieuw contactmoment'}
    >
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        {/* Type en Datum sectie */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Type & Planning
          </h3>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
                Type contact
                <span className={styles.required}>*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={`${styles.select} ${errors.type ? styles.error : ''}`}
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.type && <span className={styles.errorText}>{errors.type}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Datum
                <span className={styles.required}>*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={`${styles.input} ${errors.date ? styles.error : ''}`}
              />
              {errors.date && <span className={styles.errorText}>{errors.date}</span>}
            </div>
          </div>
        </div>

        {/* Inhoud sectie */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Inhoud
          </h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Onderwerp
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Bijv. Kwartaaloverleg projectvoortgang"
              className={`${styles.input} ${errors.subject ? styles.error : ''}`}
            />
            {errors.subject && <span className={styles.errorText}>{errors.subject}</span>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Beschrijving
              <span className={styles.helpText}>(Details van het gesprek)</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Besproken onderwerpen, belangrijke punten, afspraken..."
              className={styles.textarea}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Uitkomst
              <span className={styles.helpText}>(Resultaat van het contact)</span>
            </label>
            <textarea
              name="outcome"
              value={formData.outcome}
              onChange={handleChange}
              rows={2}
              placeholder="Concrete afspraken, vervolgstappen..."
              className={styles.textarea}
            />
          </div>
        </div>

        {/* Sentiment & Follow-up sectie */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
            Sentiment & Opvolging
          </h3>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Sentiment
              </label>
              <select
                name="sentiment"
                value={formData.sentiment}
                onChange={handleChange}
                className={styles.select}
              >
                {sentimentOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Aanwezigen
                <span className={styles.helpText}>(komma gescheiden)</span>
              </label>
              <input
                type="text"
                name="participants"
                value={Array.isArray(formData.participants) ? formData.participants.join(', ') : formData.participants}
                onChange={handleChange}
                placeholder="Jan Jansen, Marie de Vries..."
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                name="followUpRequired"
                checked={formData.followUpRequired}
                onChange={handleChange}
                style={{ width: 'auto' }}
              />
              Opvolging vereist
            </label>
          </div>

          {formData.followUpRequired && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Volgende contact
              </label>
              <input
                type="date"
                name="nextContact"
                value={formData.nextContact}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className={styles.modalActions}>
          {moment && onDelete && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Weet u zeker dat u dit contactmoment wilt verwijderen?')) {
                  onDelete();
                  onClose();
                }
              }}
              className={styles.btnDanger}
              style={{ marginRight: 'auto' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Verwijderen
            </button>
          )}
          <button type="button" onClick={onClose} className={styles.btnSecondary}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Annuleren
          </button>
          <button type="submit" className={styles.btnPrimary}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            {moment ? 'Wijzigingen opslaan' : 'Moment toevoegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
};